import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import Map2GIS from './Map2GIS';
import MapYandex from './MapYandex';
import { useQuery } from '@tanstack/react-query';
import { getSetting } from '../services/settingService';

interface MapDisplayProps {
  lat: number;
  lng: number;
  label?: string;
}

const MapDisplay: React.FC<MapDisplayProps> = ({ lat, lng, label }) => {
  const [mapProvider, setMapProvider] = useState<'yandex' | '2gis'>('2gis');
  const [isLoading, setIsLoading] = useState(true);
  const [_error, _ ] = useState<string | null>(null);

  // Fetch map provider setting
  const { data: mapProviderSetting, isLoading: settingLoading } = useQuery({
    queryKey: ['setting', 'ui.map_provider'],
    queryFn: () => getSetting('ui.map_provider'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });

  // Handle setting changes
  useEffect(() => {
    if (mapProviderSetting?.value && (mapProviderSetting.value === 'yandex' || mapProviderSetting.value === '2gis')) {
      setMapProvider(mapProviderSetting.value);
    }
    setIsLoading(false);
  }, [mapProviderSetting]);

  // Handle loading state
  useEffect(() => {
    if (!settingLoading) {
      setIsLoading(false);
    }
  }, [settingLoading]);

  // Show loading while fetching settings
  if (settingLoading || isLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={300}
        border="1px solid #e0e0e0"
        borderRadius={1}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error if coordinates are invalid
  if (!lat || !lng) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={300}
        border="1px solid #e0e0e0"
        borderRadius={1}
      >
        <Alert severity="warning">Invalid coordinates</Alert>
      </Box>
    );
  }

  // Render the appropriate map component
  return (
    <Box>
      {mapProvider === 'yandex' ? (
        <MapYandex lat={lat} lng={lng} label={label} />
      ) : (
        <Map2GIS lat={lat} lng={lng} label={label} />
      )}
    </Box>
  );
};

export default MapDisplay; 